'use client';

// ContentSourceList - Display list of all content sources with type icons and status
// Supports hierarchical display of crawled pages under their parent URLs

import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useTranslations } from 'next-intl';
import type { ContentSource, SourceType } from '../../types/content-source';
import { SortableSourceList } from './SortableSourceList';

type SortOption = 'date' | 'type' | 'size';

// Type order for sorting by type
const TYPE_ORDER: Record<SourceType, number> = {
  text: 0,
  clarification: 1,
  file: 2,
  image: 3,
  url: 4,
  google_docs: 5,
  google_sheets: 6,
};

/** Groups content by parent - returns top-level items and a map of children by parent ID */
function groupByParent(sources: ContentSource[]): {
  topLevel: ContentSource[];
  childrenByParent: Map<string, ContentSource[]>;
} {
  const topLevel: ContentSource[] = [];
  const childrenByParent = new Map<string, ContentSource[]>();

  for (const source of sources) {
    if (source.parentContentId) {
      const children = childrenByParent.get(source.parentContentId) || [];
      children.push(source);
      childrenByParent.set(source.parentContentId, children);
    } else {
      topLevel.push(source);
    }
  }

  return { topLevel, childrenByParent };
}

export interface ContentSourceListProps {
  /** List of content sources */
  sources: ContentSource[];
  /** Callback when order changes */
  onReorder: (orderedIds: string[]) => void;
  /** Callback when delete is clicked */
  onDelete: (id: string) => void;
  /** Callback when edit is clicked (only for text type) */
  onEdit?: ((id: string, content: string) => void) | undefined;
  /** Whether interactions are disabled */
  disabled?: boolean;
  /** Whether to show empty state */
  showEmptyState?: boolean;
  /** Whether the list is rendered inside a scrollable overflow container
   *  (e.g. desktop sidebar). When true, sticky title uses top: 0 of the
   *  container. When false, sticky title offsets below the page-level
   *  LandingHeader. Default: false. */
  inOverflowContainer?: boolean;
}

/**
 * ContentSourceList - Display all content sources with type icons and status
 *
 * Features:
 * - Shows type-specific icons (text, file, image, URL)
 * - Displays title/preview and character count
 * - Status indicators (processing, ready, error)
 * - Drag-and-drop reordering
 * - Delete individual sources
 * - Empty state message
 * - RTL support via CSS logical properties
 *
 * @example
 * ```tsx
 * <ContentSourceList
 *   sources={sources}
 *   onReorder={(ids) => reorderSources(ids)}
 *   onDelete={(id) => deleteSource(id)}
 * />
 * ```
 */
export function ContentSourceList({
  sources,
  onReorder,
  onDelete,
  onEdit,
  disabled = false,
  showEmptyState = true,
  inOverflowContainer = false,
}: ContentSourceListProps) {
  const t = useTranslations('creator.contentSources');
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  // Group sources by parent and sort
  const { topLevel, childrenByParent, sortedAll } = useMemo(() => {
    // First sort all sources
    const sorted = [...sources];
    if (sortBy === 'date') {
      sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === 'type') {
      sorted.sort((a, b) => {
        const typeCompare = TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
        if (typeCompare !== 0) return typeCompare;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
    } else if (sortBy === 'size') {
      sorted.sort((a, b) => b.characterCount - a.characterCount);
    }

    // Then group by parent
    const grouped = groupByParent(sorted);
    return { ...grouped, sortedAll: sorted };
  }, [sources, sortBy]);

  // Toggle expansion of a parent's children
  const toggleParentExpanded = (parentId: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  // Count top-level items (excluding children)
  const topLevelCount = topLevel.length;

  // Show empty state if no sources
  if (sources.length === 0) {
    if (!showEmptyState) {
      return null;
    }

    return (
      <Box
        sx={{
          py: 4,
          px: 2,
          textAlign: 'center',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'action.hover',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('empty')}
        </Typography>
      </Box>
    );
  }

  // Check if any parent has children (hierarchical view needed)
  const hasHierarchy = childrenByParent.size > 0;

  return (
    <>
      {/* Section header with sort dropdown */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          position: 'sticky',
          // In a scrollable container (desktop sidebar): stick to its top.
          // In page flow (mobile): offset below the sticky LandingHeader.
          top: inOverflowContainer ? 0 : ({ xs: '56px', sm: '64px' } as const),
          zIndex: 2,
          backgroundColor: 'background.paper',
          py: 1,
          mx: -1,
          px: 1,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {t('title')} ({sources.length})
        </Typography>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            disabled={disabled}
            sx={{
              fontSize: '0.75rem',
              '& .MuiSelect-select': { py: 0.5, px: 1 },
            }}
          >
            <MenuItem value="date">{t('sort.byDate')}</MenuItem>
            <MenuItem value="type">{t('sort.byType')}</MenuItem>
            <MenuItem value="size">{t('sort.bySize')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Sortable list - shows only top-level items when hierarchy exists */}
      <SortableSourceList
        sources={hasHierarchy ? topLevel : sortedAll}
        onReorder={onReorder}
        onDelete={onDelete}
        onEdit={onEdit}
        disabled={disabled}
        {...(hasHierarchy && {
          childrenByParent,
          expandedParents,
          onToggleExpand: toggleParentExpanded,
        })}
      />
    </>
  );
}

export default ContentSourceList;
